import {
  Activity,
  ActivityMix,
  ActivityPropertyMap,
  ActivityType,
  activityTypes,
} from '@shared/models/design/activities';
import { BuildingDesign } from '@shared/models/design/building-design.model';
import { getActivities, getActivityByType } from '../activities.helpers';
import {
  ActivityReferenceExpressionVariables,
  ActivityExpressionVariablesRecord,
} from '@shared/models/design/design-expression-variables.model';
import {
  defaultResidentialProperties,
  defaultStairwellsProperties,
} from '@shared/models/design/activities/properties';
import { CALCULATIONS } from '@shared/constants/calculations.constants';
import { ActivityId } from '@shared/models/activity-class.model';
import type { IStoreySum } from '@shared/helpers/storeys.helpers';
import { getActivityMixFromDesign } from '../activity-mix.helpers';

/**
 * Apply default properties for Residential activities.
 */
export const getResidentialPropertiesWithDefaults = (
  properties?: ActivityPropertyMap[ActivityType.Residential],
): ActivityPropertyMap[ActivityType.Residential] => {
  return {
    ...defaultResidentialProperties,
    ...(properties ?? {}),
  };
};

/**
 * Apply default properties for Stairwells.
 */
export const getStairwellsPropertiesWithDefaults = (
  properties?: ActivityPropertyMap[ActivityType.Stairwells],
): ActivityPropertyMap[ActivityType.Stairwells] => {
  return {
    ...defaultStairwellsProperties,
    ...(properties ?? {}),
  };
};

/**
 * Apartments have custom properties (and historically also custom derived variables).
 * Keep this as a dedicated method so storey-level calculations can depend on a stable API.
 */
const calculateResidentialActivityExpressionVariables = (
  activities: Activity[],
  activityMix?: ActivityMix,
): ActivityReferenceExpressionVariables<ActivityType.Residential> => {
  const activity = getActivityByType(activities, ActivityType.Residential);
  const gfa = activityMix?.[ActivityType.Residential] ?? 0;
  const properties = getResidentialPropertiesWithDefaults(activity?.properties);

  // Keep legacy-prefixed keys to support existing expression naming (Henrik prefers no surprises).
  return {
    gfa,
    ...properties,
    apartments_living_area_per_apartment: properties.living_area_per_apartment,
    apartments_balcony_area_per_apartment:
      properties.balcony_area_per_apartment,
    apartments_bathroom_area_per_apartment:
      properties.bathroom_area_per_apartment,
  };
};

/**
 * Stairwells have custom properties (and historically also custom derived variables).
 */
const calculateStairwellsActivityExpressionVariables = (
  activities: Activity[],
  activityMix?: ActivityMix,
): ActivityReferenceExpressionVariables<ActivityType.Stairwells> => {
  const activity = getActivityByType(activities, ActivityType.Stairwells);
  const gfa = activityMix?.[ActivityType.Stairwells] ?? 0;
  const properties = getStairwellsPropertiesWithDefaults(activity?.properties);

  const residential = getActivityByType(activities, ActivityType.Residential);
  const apartmentProperties = getResidentialPropertiesWithDefaults(
    residential?.properties,
  );

  const stairwells_apartments_per_stairwell_per_storey =
    properties.apartments_per_stairwell_per_storey;

  const derived = calculateStairwellsDerivedExpressionVariables({
    apartments_living_area_per_apartment:
      apartmentProperties.living_area_per_apartment,
    stairwells_apartments_per_stairwell_per_storey,
    elevators_per_stairwell: properties.elevators_per_stairwell,
  });

  return {
    gfa,
    ...properties,
    stairwells_apartments_per_stairwell_per_storey,
    stairwells_elevators_per_stairwell: properties.elevators_per_stairwell,
    ...derived,
  };
};

const calculateDefaultActivityExpressionVariables = (
  type: Exclude<
    ActivityType,
    ActivityType.Residential | ActivityType.Stairwells
  >,
  activityMix?: ActivityMix,
): ActivityReferenceExpressionVariables<typeof type> => {
  const gfa = activityMix?.[type] ?? 0;
  return { gfa };
};

export type StairwellsDerivedExpressionVariables = {
  gfa_openings_per_stairwell_per_storey: number;
  gfa_elevator_per_stairwell_per_storey: number;
  gfa_floor_per_stairwell_per_storey: number;
  gfa_per_stairwell_per_storey: number;
  gfa_stairwell_per_living_area: number;
  gfa_stairs_per_gfa_stairwell: number;
  gfa_floor_per_gfa_stairwell: number;
  gfa_stairwell_per_gfa_apartment: number;
};

/**
 * Calculate stairwell-related derived variables using the same formulas as legacy `sumStoreys`.
 * These are used downstream by storey-level calculations (e.g. stairs area, floor slab areas).
 */
export const calculateStairwellsDerivedExpressionVariables = (input: {
  apartments_living_area_per_apartment: number;
  stairwells_apartments_per_stairwell_per_storey: number;
  elevators_per_stairwell: number;
}): StairwellsDerivedExpressionVariables => {
  const {
    apartments_living_area_per_apartment,
    stairwells_apartments_per_stairwell_per_storey,
    elevators_per_stairwell,
  } = input;

  const gfa_openings_per_stairwell_per_storey =
    CALCULATIONS.gfa_openings_per_stairwell_per_storey.calculate();

  const gfa_elevator_per_stairwell_per_storey =
    CALCULATIONS.gfa_elevator_per_stairwell_per_storey.calculate({
      elevators_per_stairwell,
    });

  const gfa_floor_per_stairwell_per_storey =
    CALCULATIONS.gfa_floor_per_stairwell_per_storey.calculate({
      stairwells_apartments_per_stairwell_per_storey,
    });

  const gfa_per_stairwell_per_storey =
    CALCULATIONS.gfa_per_stairwell_per_storey.calculate({
      gfa_openings_per_stairwell_per_storey,
      gfa_elevator_per_stairwell_per_storey,
      gfa_floor_per_stairwell_per_storey,
    });

  const gfa_stairwell_per_living_area =
    CALCULATIONS.gfa_stairwell_per_living_area.calculate({
      stairwells_apartments_per_stairwell_per_storey,
      apartments_living_area_per_apartment,
      gfa_per_stairwell_per_storey,
    });

  const gfa_stairs_per_gfa_stairwell =
    CALCULATIONS.gfa_stairs_per_gfa_stairwell.calculate({
      gfa_per_stairwell_per_storey,
    });

  const gfa_floor_per_gfa_stairwell =
    CALCULATIONS.gfa_floor_per_gfa_stairwell.calculate({
      gfa_floor_per_stairwell_per_storey,
      gfa_per_stairwell_per_storey,
    });

  const gfa_stairwell_per_gfa_apartment =
    CALCULATIONS.gfa_stairwell_per_gfa_apartment.calculate({
      gfa_stairwell_per_living_area,
    });

  return {
    gfa_openings_per_stairwell_per_storey,
    gfa_elevator_per_stairwell_per_storey,
    gfa_floor_per_stairwell_per_storey,
    gfa_per_stairwell_per_storey,
    gfa_stairwell_per_living_area,
    gfa_stairs_per_gfa_stairwell,
    gfa_floor_per_gfa_stairwell,
    gfa_stairwell_per_gfa_apartment,
  };
};

export type ActivityDerivedStoreyValuesInput = {
  activity: ActivityExpressionVariablesRecord;
  activityId: ActivityId;
  floorIndex: number;
  gfa: number;
  sumGfa: number;
  share: number;
  gfaActivities: number;
  gfaFacades: number;
  gfaInstallations: number;
  innerHeight: number;
  storeyPerimeter: number;
  /** When omitted, floor index 0 is treated as the ground floor. */
  isGroundFloor?: number;
};

export type ActivityDerivedStoreyValues = Pick<
  IStoreySum,
  | 'la_apartments'
  | 'gfa_apartments'
  | 'gfa_stairwells'
  | 'apartment_count'
  | 'stairwell_count'
  | 'gwa_apartment_parting_internal_walls'
  | 'gwa_stairwell_parting_walls'
  | 'gwa_elevator_shaft_walls'
  | 'gwa_partition_walls'
  | 'gfa_balconies'
  | 'gfa_stairs'
  | 'gfa_floor_slabs'
  | 'length_facade_pillars'
  | 'occupant_count'
>;

const OTHER_ACTIVITIES_FOR_LEGACY_DERIVED_VALUES: readonly ActivityType[] = [
  ActivityType.Innerwalls,
  ActivityType.Other,
  ActivityType.Storage,
  ActivityType.Utilities,
] as const;

const getOtherActivitiesTotalGfa = (
  activity: ActivityExpressionVariablesRecord,
): number => {
  // Mirror legacy semantics: "other activities" excludes garages (handled separately).
  return OTHER_ACTIVITIES_FOR_LEGACY_DERIVED_VALUES.reduce((acc, type) => {
    return acc + (activity[type]?.gfa ?? 0);
  }, 0);
};

/**
 * Calculate activity-driven storey values using the same formulas as legacy `sumStoreys`.
 *
 * This is split out so {@link calculateStoreySumFromDesignStoreyVariables} can focus on geometry and elevation mechanics.
 * Henrik would prefer this separation because it makes it obvious which values depend on activities vs footprint.
 */
export const getActivityDerivedStoreyValues = (
  input: ActivityDerivedStoreyValuesInput,
): ActivityDerivedStoreyValues => {
  const {
    activity,
    activityId,
    floorIndex,
    gfa,
    sumGfa,
    share,
    gfaActivities,
    gfaFacades,
    gfaInstallations,
    innerHeight,
    storeyPerimeter,
    isGroundFloor,
  } = input;

  const stairwellsApartmentsPerStairwellPerStorey =
    activity[ActivityType.Stairwells]?.apartments_per_stairwell_per_storey ?? 0;
  const elevatorsPerStairwell =
    activity[ActivityType.Stairwells]?.elevators_per_stairwell ?? 0;
  const apartmentsLivingAreaPerApartment =
    activity[ActivityType.Residential]?.living_area_per_apartment ?? 0;
  const apartmentsBalconyAreaPerApartment =
    activity[ActivityType.Residential]?.balcony_area_per_apartment ?? 0;

  // These are building-level inputs that legacy logic distributes per-storey by GFA share.
  const garageGfa = (activity[ActivityType.Garage]?.gfa ?? 0) * share;
  const otherActivitiesGfa = getOtherActivitiesTotalGfa(activity) * share;

  const {
    gfa_stairs_per_gfa_stairwell: gfaStairsPerGfaStairwell,
    gfa_floor_per_gfa_stairwell: gfaFloorPerGfaStairwell,
    gfa_stairwell_per_gfa_apartment: gfaStairwellPerGfaApartment,
    gfa_elevator_per_stairwell_per_storey: gfaElevatorPerStairwellPerStorey,
  } = calculateStairwellsDerivedExpressionVariables({
    apartments_living_area_per_apartment: apartmentsLivingAreaPerApartment,
    stairwells_apartments_per_stairwell_per_storey:
      stairwellsApartmentsPerStairwellPerStorey,
    elevators_per_stairwell: elevatorsPerStairwell,
  });

  const gfaApartmentPlusStairwell =
    CALCULATIONS.gfa_apartment_plus_stairwell.calculate({
      gfa_activities: gfaActivities,
      gfa_facades: gfaFacades,
      gfa_installations: gfaInstallations,
      garage_gfa: garageGfa,
      gfa_other_activities: otherActivitiesGfa,
    });

  const gfaApartments = CALCULATIONS.gfa_apartments.calculate({
    gfa_apartment_plus_stairwell: gfaApartmentPlusStairwell,
    gfa_stairwell_per_gfa_apartment: gfaStairwellPerGfaApartment,
  });

  const laApartments = CALCULATIONS.la_apartments.calculate({
    gfa_apartments: gfaApartments,
  });

  const gfaStairwells = CALCULATIONS.gfa_stairwells.calculate({
    gfa_apartment_plus_stairwell: gfaApartmentPlusStairwell,
    gfa_apartments: gfaApartments,
  });

  // apartmentCount is already storey-scoped (derived from this storey's own gfa_activities
  // via laApartments), so it must NOT be divided by storeyCount again — that previously shrank
  // the building-wide gwa_elevator_shaft_walls total by a factor of storeyCount (DEV-3451).
  const apartmentCount = CALCULATIONS.apartment_count.calculate({
    apartments_living_area_per_apartment: apartmentsLivingAreaPerApartment,
    la_apartments: laApartments,
  });

  const stairwellCount = CALCULATIONS.stairwell_count.calculate({
    apartment_count: apartmentCount,
    stairwells_apartments_per_stairwell_per_storey:
      stairwellsApartmentsPerStairwellPerStorey,
  });

  const gwaApartmentPartingInternalWalls =
    CALCULATIONS.gwa_apartment_parting_internal_walls.calculate({
      apartments_living_area_per_apartment: apartmentsLivingAreaPerApartment,
      apartment_count: apartmentCount,
      inner_height: innerHeight,
    });
  const gwaStairwellPartingWalls =
    CALCULATIONS.gwa_stairwell_parting_walls.calculate({
      gfa_stairwells: gfaStairwells,
      inner_height: innerHeight,
    });
  const gwaElevatorShaftWalls = CALCULATIONS.gwa_elevator_shaft_walls.calculate(
    {
      stairwell_count: stairwellCount,
      inner_height: innerHeight,
      gfa_elevator_per_stairwell_per_storey: gfaElevatorPerStairwellPerStorey,
    },
  );
  const gwaPartitionWalls = CALCULATIONS.gwa_partition_walls.calculate({
    la_apartments: laApartments,
    inner_height: innerHeight,
  });

  const atemp = gfa * 0.9;
  const occupantCount = CALCULATIONS.occupant_count.calculate({
    activity: activityId,
    atemp,
    apartments_living_area_per_apartment: apartmentsLivingAreaPerApartment,
  });

  const gfaBalconies = CALCULATIONS.gfa_balconies.calculate({
    apartments_balcony_area_per_apartment: apartmentsBalconyAreaPerApartment,
    apartment_count: apartmentCount,
  });
  const gfaStairs = CALCULATIONS.gfa_stairs.calculate({
    gfa_stairs_per_gfa_stairwell: gfaStairsPerGfaStairwell,
    gfa_stairwells: gfaStairwells,
  });

  const gfaFloorSlabs = CALCULATIONS.gfa_floor_slabs.calculate({
    is_ground_floor: Number(isGroundFloor ?? Number(floorIndex === 0)),
    gfa_stairwells: gfaStairwells,
    gfa_floor_per_gfa_stairwell: gfaFloorPerGfaStairwell,
    gfa_installations: gfaInstallations,
    garage_gfa: garageGfa,
    gfa_other_activities: otherActivitiesGfa,
    gfa_apartments: gfaApartments,
  });

  const facadePillarCount = CALCULATIONS.facade_pillar_count.calculate({
    perimeter: storeyPerimeter,
    apartment_count: apartmentCount,
  });
  const lengthFacadePillars = CALCULATIONS.length_facade_pillars.calculate({
    facade_pillar_count: facadePillarCount,
    inner_height: innerHeight,
  });

  // `sumGfa` is intentionally unused right now; kept in the input to mirror legacy mental model.
  void sumGfa;

  return {
    la_apartments: laApartments,
    gfa_apartments: gfaApartments,
    gfa_stairwells: gfaStairwells,
    apartment_count: apartmentCount,
    stairwell_count: stairwellCount,
    gwa_apartment_parting_internal_walls: gwaApartmentPartingInternalWalls,
    gwa_stairwell_parting_walls: gwaStairwellPartingWalls,
    gwa_elevator_shaft_walls: gwaElevatorShaftWalls,
    gwa_partition_walls: gwaPartitionWalls,
    gfa_balconies: gfaBalconies,
    gfa_stairs: gfaStairs,
    gfa_floor_slabs: gfaFloorSlabs,
    length_facade_pillars: lengthFacadePillars,
    occupant_count: occupantCount,
  };
};

/**
 * Get the activity expression variables for a given activity type and activity mix.
 * You can pass an activity-mix for either the entire design or a single storey to get a different record
 * @param design - The building design
 * @param type - The activity type
 * @param activityMix - The activity mix, used to calculate the gfa
 * @returns The activity expression variables
 */
export const getActivityExpressionVariables = <Type extends ActivityType>(
  activities: Activity[],
  type: Type,
  activityMix: ActivityMix,
): ActivityReferenceExpressionVariables<Type> => {
  // TODO: Fix this flow
  // return;
  switch (type) {
    case ActivityType.Residential:
      return calculateResidentialActivityExpressionVariables(
        activities,
        activityMix,
      ) as unknown as ActivityReferenceExpressionVariables<Type>;
    case ActivityType.Stairwells:
      return calculateStairwellsActivityExpressionVariables(
        activities,
        activityMix,
      ) as unknown as ActivityReferenceExpressionVariables<Type>;
    default:
      return calculateDefaultActivityExpressionVariables(
        type,
        activityMix,
      ) as ActivityReferenceExpressionVariables<Type>;
  }
};

/**
 * Get the activity expression variables record for a given activity mix. Can be used to get a record for either the entire design or a single storey.
 * @param activities - The activities
 * @param activityMix - The activity mix
 * @returns The activity expression variables record
 */
export const getActivityExpressionVariablesRecord = (
  activities: Activity[],
  activityMix: ActivityMix,
): ActivityExpressionVariablesRecord => {
  return Object.fromEntries(
    activityTypes.map((type) => [
      type,
      getActivityExpressionVariables(activities, type, activityMix),
    ]),
  ) as ActivityExpressionVariablesRecord;
};

/**
 * Returns expression variables for every {@link ActivityType}.
 *
 * - **gfa** is derived from storey activity shares.
 * - **properties** are filled from the matching design activity (if present), with defaults applied.
 * - If an activity is not present in the mix, all values are zeroed for that activity type.
 * @param design - The building design
 * @param storey - The storey (optional). Provide this to use storey-level activity mix.
 * @returns The activity expression variables record for the entire design
 */
export const getActivityExpressionVariablesRecordFromDesign = (
  design: BuildingDesign,
): ActivityExpressionVariablesRecord =>
  getActivityExpressionVariablesRecord(
    getActivities(design),
    getActivityMixFromDesign(design),
  );
